import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
  onAuthStateChanged
} from "firebase/auth";
import { auth } from "../../firebase";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, X, ArrowRight, Loader2, Globe, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

// Import pixel tracking functions
import { trackSignup, trackLead, trackLogin, trackCustomEvent, trackEmailFocus, trackGoogleButtonClick, trackEmailVerificationSent } from '../../utils/pixels';

// Import Brazil detection
import { isBrazilianUser } from '../../utils/detectBrazil';

// Import email allowlist
import { isEmailDenied } from '../../utils/emailAllowlist';

// Import phone verification

// Translation object (keeping existing translations)
const translations = {
  ENG: {
    // Main headers
    brandName: "PickStar",
    tagline: "Your Personal AI-powered Sports Intelligence Hub",
    whyChoose: "Why Choose PickStar?",

    // Benefits
    benefits: [
      "Unmatched AI-powered sports insights",
      "Personalized game-specific insights",
      "Stats, simulation analysis, and research tools",
      "Free and Instant Access, No Sketchy BS"
    ],

    // Email verification notice
    emailVerificationNotice: "You'll need to verify your email to access PickStar.",
    emailVerificationRecommendation: "We recommend Gmail for instant automatic verification.",
    
    // Testimonial
    testimonial: "PickStar's proprietary AI platform creates an unmatched personalized agents that are 100% free to use and optimized to help you make better decisions on your sports entertainment. Your Plays, Your PickStar.",
    testimonialAuthor: "- PickStar Team",
    
    // Form headers
    signUpHeader: "Free to Sign-Up",
    signInHeader: "Welcome back",
    
    // Form fields
    emailPlaceholder: "Email address",
    passwordPlaceholder: "Password",
    forgotPassword: "Forgot password?",
    
    // Buttons
    createAccount: "Create 100% Free Account",
    signIn: "Sign In",
    processing: "Processing...",
    continueWithGoogle: "Continue with Google",
    
    // Toggle links
    alreadyHaveAccount: "Already have an account? Sign in",
    dontHaveAccount: "Don't have an account? Sign up",
    
    // Divider
    orDivider: "or",
    
    // Messages
    resetLinkSent: "Reset link sent to your email",
    
    // Terms
    termsText: "By signing up, you agree to our",
    termsLink: "Terms & Conditions",
    andText: "and",
    privacyLink: "Privacy Policy",
    
    // Footer
    privacyPolicy: "Privacy Policy",
    termsConditions: "Terms & Conditions",
    copyright: "© 2026 PickStar AI",
    
    // Error messages
    emailPasswordRequired: "Please enter both email and password.",
    emailAlreadyInUse: "This email is already registered. Try signing in instead.",
    weakPassword: "Password should be at least 6 characters.",
    invalidCredentials: "Invalid email or password. Please try again.",
    enterEmailFirst: "Please enter your email address first.",
    emailProviderNotAllowed: "Please use Gmail, Yahoo, Outlook, or another major email provider. Need help? Contact support@pickstarai.com",

    // Removed email verification - no longer needed
  },
  BRL: {
    // Main headers
    brandName: "PickStar",
    tagline: "Seu Hub Pessoal de Inteligência Esportiva com IA",
    whyChoose: "Por que Escolher o PickStar?",

    // Benefits
    benefits: [
      "Insights esportivos incomparáveis com IA",
      "Insights personalizados específicos do jogo",
      "Estatísticas, análise de simulação e ferramentas de pesquisa",
      "Acesso Gratuito e Instantâneo, Sem Pegadinhas"
    ],

    // Email verification notice
    emailVerificationNotice: "Você precisará verificar seu e-mail para acessar o PickStar.",
    emailVerificationRecommendation: "Recomendamos Gmail para verificação automática instantânea.",
    
    // Testimonial
    testimonial: "A plataforma de IA proprietária do PickStar cria agentes personalizados incomparáveis que são 100% gratuitos de usar e otimizados para ajudá-lo a tomar melhores decisões no seu entretenimento esportivo. Suas Jogadas, Sua PickStar.",
    testimonialAuthor: "- Equipe PickStar",
    
    // Form headers
    signUpHeader: "Cadastro Gratuito",
    signInHeader: "Bem-vindo de volta",
    
    // Form fields
    emailPlaceholder: "Endereço de email",
    passwordPlaceholder: "Senha",
    forgotPassword: "Esqueceu a senha?",
    
    // Buttons
    createAccount: "Criar Conta 100% Gratuita",
    signIn: "Entrar",
    processing: "Processando...",
    continueWithGoogle: "Continuar com Google",
    
    // Toggle links
    alreadyHaveAccount: "Já tem uma conta? Entre",
    dontHaveAccount: "Não tem uma conta? Cadastre-se",
    
    // Divider
    orDivider: "ou",
    
    // Messages
    resetLinkSent: "Link de redefinição enviado para seu email",
    
    // Terms
    termsText: "Ao se cadastrar, você concorda com nossos",
    termsLink: "Termos e Condições",
    andText: "e",
    privacyLink: "Política de Privacidade",
    
    // Footer
    privacyPolicy: "Política de Privacidade",
    termsConditions: "Termos e Condições",
    copyright: "© 2026 PickStar AI",
    
    // Error messages
    emailPasswordRequired: "Por favor, insira email e senha.",
    emailAlreadyInUse: "Este email já está registrado. Tente fazer login.",
    weakPassword: "A senha deve ter pelo menos 6 caracteres.",
    invalidCredentials: "Email ou senha inválidos. Tente novamente.",
    enterEmailFirst: "Por favor, insira seu endereço de email primeiro.",
    emailProviderNotAllowed: "Use Gmail, Yahoo, Outlook ou outro provedor principal. Precisa de ajuda? Contate support@pickstarai.com",

    // Removed email verification - no longer needed
  }
};

// Enhanced email validation function
const validateEmail = (email) => {
  // Remove whitespace
  const trimmedEmail = email.trim().toLowerCase();

  // Check basic format with regex
  const emailRegex = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  if (!emailRegex.test(trimmedEmail)) {
    return { valid: false, error: 'Please use a valid email address' };
  }

  // Check for common typos in popular domains
  const commonTypos = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'gmail.co': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'outlok.com': 'outlook.com',
    'outlok.com': 'outlook.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
  };

  const domain = trimmedEmail.split('@')[1];
  if (commonTypos[domain]) {
    return { valid: false, error: `Did you mean @${commonTypos[domain]}?`, suggestion: trimmedEmail.replace(domain, commonTypos[domain]) };
  }

  // Check for disposable email domains
  const disposableEmailDomains = [
    'tempmail.com', 'throwaway.email', '10minutemail.com', 'mailinator.com',
    'guerrillamail.com', 'maildrop.cc', 'trashmail.com', 'temp-mail.org',
    'fakeinbox.com', 'sharklasers.com', 'guerrillamail.info', 'grr.la'
  ];

  if (disposableEmailDomains.includes(domain)) {
    return { valid: false, error: 'Please use a valid email address' };
  }

  // Check minimum length requirements
  if (trimmedEmail.length < 5) {
    return { valid: false, error: 'Email address is too short' };
  }

  // Check for missing TLD
  if (!domain || !domain.includes('.')) {
    return { valid: false, error: 'Email must include a domain (e.g., @gmail.com)' };
  }

  return { valid: true, email: trimmedEmail };
};

const GOOGLE_INTENT_STORAGE_KEY = 'pickstar_google_auth_intent';

const storeGoogleIntent = (intent) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(GOOGLE_INTENT_STORAGE_KEY, intent);
  } catch (error) {
    console.log("error while storing google intention")
  }
};

const consumeGoogleIntent = () => {
  if (typeof window === 'undefined') return null;
  try {
    const intent = window.sessionStorage.getItem(GOOGLE_INTENT_STORAGE_KEY);
    if (intent) {
      window.sessionStorage.removeItem(GOOGLE_INTENT_STORAGE_KEY);
      return intent;
    }
  } catch (error) {
    console.log("error while consuming google intention")
  }
  return null;
};

const shouldUseRedirectForGoogle = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/i.test(ua) || (ua.includes('Mac') && 'ontouchend' in window);
  return isIOS;
};

const Login = () => {

  // Add useLocation to get state from preview homepage
  const location = useLocation();
  const navigate = useNavigate();
  
  // Enhanced language detection using detectBrazil utility
  const getInitialLanguage = () => {
    // First check if user is Brazilian using the detection utility
    if (isBrazilianUser()) {
      return 'BRL';
    }

    // Check URL parameter (?lang=pt-br)
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam === 'pt-br' || langParam === 'pt') {
      return 'BRL';
    }

    // Check React Router state (from /pt-br homepage)
    if (location.state?.language === 'BRL') {
      return 'BRL';
    }

    // Default to English
    return 'ENG';
  };

  // Language state - simple and clean
  const [language, setLanguage] = useState(getInitialLanguage());
  const t = translations[language]; // Translation helper
  
  // Save language preference when it changes (remove localStorage in artifacts)
  useEffect(() => {
    // In your real app, uncomment this line:
    // localStorage.setItem('pickstar_language', language);
  }, [language]);
  
  // Refs for interactive elements
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  
  // Form state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  
  // Check if we received state from preview homepage
  const [isSignUp, setIsSignUp] = useState(
    location.state?.isSignUp !== undefined ? location.state.isSignUp : true
  );
  
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // UI state
  const [isHoveringGoogle, setIsHoveringGoogle] = useState(false);
  const [isHoveringSubmit, setIsHoveringSubmit] = useState(false);

  // Track when user first interacts with form (Lead event)
  const [hasTrackedLead, setHasTrackedLead] = useState(false);

  // Track lead when user starts filling form
  const handleFormInteraction = () => {
    if (!hasTrackedLead && isSignUp) {
      trackLead();
      setHasTrackedLead(true);
    }
  };

  // Simple auth state handler - just redirect authenticated users
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
       navigate('/home');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fromLandingPage = location.state?.fromLandingPage || null;

  const handleGoogleAuthSuccess = useCallback((result, intentOverride = null) => {
    if (!result?.user) return;

    const user = result.user;
    const isNewUser = result._tokenResponse?.isNewUser || false;
    const intent = intentOverride === 'signup' || intentOverride === 'signin' ? intentOverride : null;
    const shouldTrackSignup = isNewUser || intent === 'signup';

    console.log('🔐 Google auth complete:', {
      isNewUser,
      intent,
      uid: user.uid,
      email: user.email
    });

    if (shouldTrackSignup) {
      trackSignup('google', user.uid, {
        language: language,
        fromLandingPage: fromLandingPage
      });

      trackCustomEvent('GoogleSignup', {
        user_id: user.uid,
        email: user.email,
        language: language,
        timestamp: new Date().toISOString()
      });
    } else {
      trackLogin('google');
    }

    navigate('/home');
  }, [fromLandingPage, language, navigate]);

  useEffect(() => {
    let isActive = true;

    const resolveRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);

        if (!isActive) return;

        if (result) {
          const storedIntent = consumeGoogleIntent();
          handleGoogleAuthSuccess(result, storedIntent);
          return;
        }

        // If redirect completed but no result is returned, fall back to auth state.
        if (auth.currentUser) {
          navigate('/home');
        }
      } catch (error) {
        if (!isActive) return;
        setError(error.message);

        trackCustomEvent('AuthenticationFailed', {
          method: 'google',
          error: error.code,
          language: language
        });
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    resolveRedirectResult();

    return () => {
      isActive = false;
    };
  }, [handleGoogleAuthSuccess, language, navigate]);

  // Track email focus for micro-conversions
  const handleEmailFocus = () => {
    handleFormInteraction();
    trackEmailFocus();
  };

  // Handle canvas-based particle visualization (keeping existing animation)
  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    let particles = [];
    const particleCount = 35;
    
    // Initialize particles
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        radius: Math.random() * 4 + 1,
        color: i % 4 === 0 ? '#10b981' : i % 4 === 1 ? '#06b6d4' : i % 4 === 2 ? '#8b5cf6' : '#f472b6',
        velocity: {
          x: (Math.random() - 0.5) * 1.5,
          y: (Math.random() - 0.5) * 1.5
        },
        alpha: Math.random() * 0.6 + 0.4
      });
    }
    
    // Animation function - simplified to just particles
    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      
      // Draw subtle grid lines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.03)';
      ctx.lineWidth = 1;
      
      // Horizontal lines - fewer, more subtle
      for (let i = 0; i < 4; i++) {
        const y = height * i / 3;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
      }
      
      // Vertical lines - fewer, more subtle
      for (let i = 0; i < 5; i++) {
        const x = width * i / 4;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }
      
      // Update and draw particles
      particles.forEach(particle => {
        particle.x += particle.velocity.x;
        particle.y += particle.velocity.y;
        
        // Bounce off edges
        if (particle.x < 0 || particle.x > width) {
          particle.velocity.x = -particle.velocity.x;
        }
        
        if (particle.y < 0 || particle.y > height) {
          particle.velocity.y = -particle.velocity.y;
        }
        
        // Draw particle with glow effect
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color + Math.floor(particle.alpha * 255).toString(16).padStart(2, '0');
        ctx.fill();
        
        // Add subtle glow to larger particles
        if (particle.radius > 3) {
          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.radius * 2, 0, Math.PI * 2);
          ctx.fillStyle = particle.color + '20';
          ctx.fill();
        }
      });
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Authentication handlers with Facebook pixel tracking
  const handleGoogleSignIn = async () => {
    // Track Google button click
    trackGoogleButtonClick();
    
    setIsLoading(true);
    setError("");
    const provider = new GoogleAuthProvider();
    const intent = isSignUp ? 'signup' : 'signin';
    
    try {
      if (shouldUseRedirectForGoogle()) {
        storeGoogleIntent(intent);
        await signInWithRedirect(auth, provider);
        return;
      }

      const result = await signInWithPopup(auth, provider);
      handleGoogleAuthSuccess(result, intent);
      
    } catch (error) {
      setError(error.message);
      
      // Track failed authentication attempt
      trackCustomEvent('AuthenticationFailed', {
        method: 'google',
        error: error.code,
        language: language
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!email || !password) {
      setError(t.emailPasswordRequired);
      setIsLoading(false);
      return;
    }

    // Validate email format
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setError(emailValidation.error);
      // If there's a suggestion, optionally set it
      if (emailValidation.suggestion) {
        setEmail(emailValidation.suggestion);
      }
      setIsLoading(false);
      return;
    }

    // Check if email domain is denied (only for signup)
    if (isSignUp && isEmailDenied(emailValidation.email)) {
      setError(t.emailProviderNotAllowed);
      setIsLoading(false);
      return;
    }

    try {
      let result;
      if (isSignUp) {
        // Use the validated email from validation result
        result = await createUserWithEmailAndPassword(auth, emailValidation.email, password);
        
        // ONLY send verification email for email/password signups (not Google)
        if (!result.user.emailVerified) {
          // Set language for email verification based on user's language choice
          auth.languageCode = language === 'BRL' ? 'pt' : 'en';
          
          await sendEmailVerification(result.user, {
            url: window.location.origin,
            handleCodeInApp: false
          });
          
          // Track that verification email was sent
          trackEmailVerificationSent(result.user.uid);
        }
        
        // Track successful email signup with additional context
        trackSignup('email', result.user.uid, {
          language: language,
          fromLandingPage: location.state?.fromLandingPage || null // '/arena' or '/pt-br'
        });

        // Track custom event for email signup
        trackCustomEvent('EmailSignup', {
          user_id: result.user.uid,
          email: email.trim(),
          language: language,
          timestamp: new Date().toISOString()
        });

        // Navigate to home - EmailVerificationGuard will handle verification
        navigate('/home');
        
      } else {
        // Use the validated email from validation result
        result = await signInWithEmailAndPassword(auth, emailValidation.email, password);
        
        // Track successful login
        trackLogin('email');
        
        // Navigate to home - EmailVerificationGuard will handle verification
        navigate('/home');
      }
      
    } catch (error) {
      // Handle errors and track failed attempts
      let errorMessage = error.message;
      
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = t.emailAlreadyInUse;
      } else if (error.code === 'auth/weak-password') {
        errorMessage = t.weakPassword;
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessage = t.invalidCredentials;
      }
      
      setError(errorMessage);
      
      // Track failed authentication attempt
      trackCustomEvent('AuthenticationFailed', {
        method: 'email',
        error: error.code,
        is_signup: isSignUp,
        language: language
      });
      
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError(t.enterEmailFirst);
      return;
    }

    // Validate email format before sending reset
    const emailValidation = validateEmail(email);
    if (!emailValidation.valid) {
      setError(emailValidation.error);
      if (emailValidation.suggestion) {
        setEmail(emailValidation.suggestion);
      }
      return;
    }

    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, emailValidation.email);
      setError("");
      setResetSent(true);
      
      // Track password reset request
      trackCustomEvent('PasswordResetRequested', {
        email: email.trim(),
        language: language
      });
      
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Track when switching between sign up and sign in
  const handleToggleMode = () => {
    const newMode = !isSignUp;
    setIsSignUp(newMode);
    setError("");
    setResetSent(false);
    setHasTrackedLead(false); // Reset lead tracking
    
    // Track mode switching
    trackCustomEvent('AuthModeChanged', {
      new_mode: newMode ? 'signup' : 'signin',
      language: language
    });
  };

  // Animation variants (keeping existing)
  const backgroundVariants = {
    animate: {
      backgroundPosition: ['0% 0%', '100% 100%'],
      transition: {
        repeat: Infinity,
        repeatType: 'mirror',
        duration: 30,
        ease: 'linear'
      }
    }
  };


  return (
    <>
      <Helmet>
        <title>Login - PickStar AI</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="description" content="Login to PickStar AI to access your sports betting predictions and analysis." />
      </Helmet>
      <div
        ref={containerRef}
        className="min-h-screen flex flex-col items-center justify-center bg-black/95 relative overflow-hidden"
      >
      {/* Back Navigation - Top Left */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute top-4 left-4 z-20"
      >
        <Link
          to="/"
          className="flex items-center gap-2 text-white/60 hover:text-white transition-all duration-300 text-sm font-medium group"
        >
          <ArrowRight className="w-4 h-4 rotate-180 group-hover:-translate-x-1 transition-transform duration-300" />
          <span>Back</span>
        </Link>
      </motion.div>

      {/* Language Toggle - Top Right on Desktop, Below Logo on Mobile */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="absolute top-4 right-4 z-20 hidden sm:block"
      >
        <div className="flex items-center gap-2 backdrop-blur-xl bg-white/[0.03] rounded-xl p-2 border border-white/[0.05]">
          <Globe className="w-4 h-4 text-emerald-400" />
          <button
            onClick={() => setLanguage('ENG')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
              language === 'ENG'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            ENG
          </button>
          <button
            onClick={() => setLanguage('BRL')}
            className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
              language === 'BRL'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                : 'text-white/60 hover:text-white/80'
            }`}
          >
            BRL
          </button>
        </div>
      </motion.div>

      {/* Terminal Glassmorphism background */}
      <motion.div
        className="absolute inset-0 bg-black"
        variants={backgroundVariants}
        animate="animate"
      />
      
      {/* Dynamic particle visualization (keeping existing) */}
      <div className="absolute inset-0 opacity-70">
        <canvas 
          ref={canvasRef} 
          width={1000} 
          height={800} 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full"
        />
      </div>
      
      {/* Floating particles for depth (keeping existing) */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="particle-container">
          {[...Array(25)].map((_, i) => (
            <div 
              key={i} 
              className="absolute rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 20 + 5}px`,
                height: `${Math.random() * 20 + 5}px`,
                background: i % 4 === 0 ? 'rgba(16, 185, 129, 0.15)' :
                            i % 4 === 1 ? 'rgba(6, 182, 212, 0.15)' :
                            i % 4 === 2 ? 'rgba(139, 92, 246, 0.15)' :
                            'rgba(244, 114, 182, 0.15)',
                animation: `float ${Math.random() * 20 + 10}s linear infinite`,
                animationDelay: `${Math.random() * 10}s`,
                opacity: Math.random() * 0.6 + 0.2
              }}
            />
          ))}
        </div>
      </div>

      {/* Dramatic gradient glows (keeping existing) */}
      <div className="absolute top-1/4 -left-1/4 w-1/2 h-1/2 bg-emerald-500/10 blur-[150px] rounded-full" />
      <div className="absolute bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-violet-500/20 blur-[150px] rounded-full" />
      <div className="absolute top-3/4 right-1/4 w-1/3 h-1/3 bg-pink-500/10 blur-[120px] rounded-full" />
      
      {/* Static glow effect (keeping existing) */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-violet-500/5 to-pink-500/5 rounded-full blur-[100px]" />
      </div>

      {/* Main Content - streamlined login form */}
      <div className="relative z-10 w-full max-w-md px-4 sm:px-6 flex flex-col items-center justify-center">
        {/* Form section */}
        <div className="w-full flex flex-col items-center">
          {/* Clean Logo Section - Footer Style */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="text-center mb-6 sm:mb-8 relative w-full"
          >
            <div className="relative flex flex-col items-center justify-center w-full">
              {/* Polished logo like footer */}
              <div className="flex items-center gap-3 mb-3">
                <Zap className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-400" strokeWidth={2} />
                <h2 className="text-white font-bold text-3xl sm:text-4xl tracking-tight">
                  PickStar AI
                </h2>
              </div>

              <p className="mt-2 sm:mt-3 text-[11px] sm:text-[12px] font-mono tracking-wider text-white/50 w-full text-center uppercase">
                {t.tagline}
              </p>
            </div>
          </motion.div>

          {/* Mobile Language Toggle (keeping existing) */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="sm:hidden w-full mb-4 flex justify-center"
          >
            <div className="flex items-center gap-2 backdrop-blur-xl bg-white/[0.03] rounded-xl p-2 border border-white/[0.05]">
              <Globe className="w-4 h-4 text-emerald-400" />
              <button
                onClick={() => setLanguage('ENG')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
                  language === 'ENG' 
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' 
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                ENG
              </button>
              <button
                onClick={() => setLanguage('BRL')}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-300 ${
                  language === 'BRL' 
                    ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' 
                    : 'text-white/60 hover:text-white/80'
                }`}
              >
                BRL
              </button>
            </div>
          </motion.div>

          {/* Auth Form with premium glassmorphism */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="w-full backdrop-blur-sm bg-white/[0.02] rounded-lg p-5 sm:p-6 shadow-xl border border-white/[0.05] relative overflow-hidden"
          >
            <div className="absolute -inset-1/2 bg-gradient-to-tr from-white/5 to-transparent rotate-12 pointer-events-none" />

            {/* Form header - emphasizing quick sign-in */}
            <h3 className="text-[13px] sm:text-[14px] font-medium text-white/70 mb-4 uppercase tracking-[0.15em] font-mono">
              {isSignUp ? t.signUpHeader : t.signInHeader}
            </h3>

            {/* Google Sign-In at the top - the preferred method */}
            <motion.button
              onMouseEnter={() => setIsHoveringGoogle(true)}
              onMouseLeave={() => setIsHoveringGoogle(false)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGoogleSignIn}
              disabled={isLoading}
              className="w-full relative group overflow-visible active:scale-95 sm:active:scale-98 mb-6"
            >
              <div
                className={`pointer-events-none absolute -inset-[2px] rounded-xl z-[-1]
                  transition-all duration-500
                  ${isHoveringGoogle
                    ? 'opacity-100 bg-gradient-to-r from-violet-600 via-purple-500 to-pink-500 blur-[4px]'
                    : 'opacity-60 bg-gradient-to-r from-violet-500/50 via-purple-400/50 to-pink-400/50 blur-[2px]'
                  }`}
              />

              <div className="relative flex items-center justify-center gap-3 px-5 py-4 sm:py-4.5 rounded-xl
                bg-white text-violet-900 font-semibold shadow-lg
                hover:bg-gray-50 transition-all duration-300"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M21.8055 10.0415H21V10H12V14H17.6515C16.827 16.3285 14.6115 18 12 18C8.6865 18 6 15.3135 6 12C6 8.6865 8.6865 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C6.4775 2 2 6.4775 2 12C2 17.5225 6.4775 22 12 22C17.5225 22 22 17.5225 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z"
                    fill="#FBC02D"
                  />
                  <path
                    d="M3.15302 7.3455L6.43852 9.755C7.32752 7.554 9.48052 6 12 6C13.5295 6 14.921 6.577 15.9805 7.5195L18.809 4.691C17.023 3.0265 14.634 2 12 2C8.15902 2 4.82802 4.1685 3.15302 7.3455Z"
                    fill="#E53935"
                  />
                  <path
                    d="M12 22C14.583 22 16.93 21.0115 18.7045 19.404L15.6095 16.785C14.6055 17.5455 13.3575 18 12 18C9.39902 18 7.19052 16.3415 6.35852 14.027L3.09752 16.5395C4.75252 19.778 8.11352 22 12 22Z"
                    fill="#4CAF50"
                  />
                  <path
                    d="M21.8055 10.0415H21V10H12V14H17.6515C17.2575 15.1085 16.547 16.0765 15.608 16.7855L15.6095 16.7845L18.7045 19.4035C18.4855 19.6025 22 17 22 12C22 11.3295 21.931 10.675 21.8055 10.0415Z"
                    fill="#1565C0"
                  />
                </svg>
                <span className="tracking-wide text-base font-bold">{t.continueWithGoogle}</span>
              </div>
            </motion.button>

            <div className="relative my-5 sm:my-6 flex items-center gap-3">
              <div className="flex-1 border-t border-white/[0.08]"></div>
              <div className="text-white/50 text-sm font-medium">{t.orDivider}</div>
              <div className="flex-1 border-t border-white/[0.08]"></div>
            </div>

            {/* Reset confirmation message */}
            <AnimatePresence>
              {resetSent && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 mb-5 sm:mb-6 relative"
                >
                  <p className="text-emerald-300 text-sm font-medium">
                    {t.resetLinkSent}
                  </p>
                  <button
                    onClick={() => setResetSent(false)}
                    className="absolute top-2 right-2 text-white/50 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleEmailSignIn} className="space-y-4 sm:space-y-5">
              <div>
                <motion.input
                  whileFocus={{ scale: 1.01 }}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    // Clear error when user types
                    if (error) setError('');
                  }}
                  onFocus={handleEmailFocus} // Enhanced tracking with email focus
                  placeholder={t.emailPlaceholder}
                  className="w-full px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-white placeholder-white/30
                    focus:outline-none focus:border-emerald-500/20 focus:bg-white/[0.03]
                    transition-all duration-300 font-mono text-[12px] tracking-wider"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <motion.input
                  whileFocus={{ scale: 1.01 }}
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onFocus={handleFormInteraction} // Track lead when user starts
                  placeholder={t.passwordPlaceholder}
                  className="w-full px-4 py-3 rounded-lg bg-white/[0.02] border border-white/[0.05] text-white placeholder-white/30
                    focus:outline-none focus:border-emerald-500/20 focus:bg-white/[0.03]
                    transition-all duration-300 font-mono text-[12px] tracking-wider"
                />
                {!isSignUp && (
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-emerald-400/70 hover:text-emerald-400 text-[10px] mt-2 inline-block transition-colors font-mono uppercase tracking-wider"
                  >
                    {t.forgotPassword}
                  </button>
                )}
              </div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-red-400/70 text-[11px] bg-red-500/[0.05] rounded-lg px-3 py-2 border border-red-500/10 font-mono"
                  >
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email Verification Notice - Only show during signup */}
              {isSignUp && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2.5"
                >
                  <div className="flex items-start gap-2">
                    <Mail className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-blue-300 text-[11px] font-medium font-mono">
                        {t.emailVerificationNotice}
                      </p>
                      <p className="text-blue-300/70 text-[10px] font-mono">
                        {t.emailVerificationRecommendation}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Premium submit button with interactive effects */}
              <motion.button
                onMouseEnter={() => setIsHoveringSubmit(true)}
                onMouseLeave={() => setIsHoveringSubmit(false)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading}
                className="group w-full py-3 px-4 rounded-lg font-medium text-[12px]
                  relative overflow-hidden transition-all duration-500 focus:outline-none disabled:opacity-50
                  active:scale-95 uppercase tracking-wider font-mono"
              >
                <div
                  className={`absolute inset-0 transition-all duration-500
                    ${isHoveringSubmit ? 'bg-gradient-to-r from-emerald-500/20 to-violet-500/20' : 'bg-gradient-to-r from-emerald-500/[0.12] to-violet-500/[0.12]'}`}
                />

                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700
                    bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transform-gpu
                    ${isHoveringSubmit ? 'duration-1000' : 'duration-2000'}`}
                />

                <div className="relative flex items-center justify-center gap-2">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-white">{t.processing}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-white">{isSignUp ? t.createAccount : t.signIn}</span>
                      <ArrowRight className="w-5 h-5 text-white group-hover:translate-x-1 transition-transform duration-300" />
                    </>
                  )}
                </div>
              </motion.button>

              {/* Terms and conditions disclaimer */}
              {isSignUp && (
                <p className="mt-3 text-center text-white/20 text-[9px] tracking-wider leading-relaxed font-mono uppercase">
                  {t.termsText}{" "}
                  <Link to="/terms-and-conditions" className="text-emerald-400/60 hover:text-emerald-400 transition-colors">
                    {t.termsLink}
                  </Link>{" "}
                  {t.andText}{" "}
                  <Link to="/privacy" className="text-emerald-400/60 hover:text-emerald-400 transition-colors">
                    {t.privacyLink}
                  </Link>
                </p>
              )}
            </form>

            {/* Premium toggle for sign up/sign in */}
            <div className="mt-6 sm:mt-8 text-center">
              <motion.button
                whileHover={{ color: 'rgb(167, 139, 250)' }}
                onClick={handleToggleMode}
                className="text-emerald-400/70 hover:text-emerald-400 transition-colors text-[11px] font-mono inline-flex items-center gap-1.5 uppercase tracking-wider"
              >
                <span>{isSignUp ? t.alreadyHaveAccount : t.dontHaveAccount}</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
          </motion.div>

          {/* Minimal elegant footer (keeping existing) */}
          <motion.footer 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-6 sm:mt-8 text-center text-[10px] text-white/20 font-mono uppercase tracking-wider"
          >
            <div className="flex items-center justify-center gap-4 sm:gap-6">
              <Link to="/privacy" className="hover:text-violet-400/60 transition-colors">
                {t.privacyPolicy}
              </Link>
              <span className="text-white/10">•</span>
              <Link to="/terms-and-conditions" className="hover:text-violet-400/60 transition-colors">
                {t.termsConditions}
              </Link>
            </div>
            <p className="mt-3 sm:mt-4">
              {t.copyright}
            </p>
          </motion.footer>
        </div>
      </div>
      
      {/* Add mobile-specific CSS for floating animations (keeping existing) */}
      <style jsx="true">{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-10px) translateX(5px);
          }
          50% {
            transform: translateY(0) translateX(10px);
          }
          75% {
            transform: translateY(10px) translateX(5px);
          }
        }
        
        @media (max-width: 640px) {
          .particle-container div {
            animation-duration: 15s !important;
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default Login;
